/* eslint-disable @next/next/no-img-element */

'use client'

import { AnimatePresence, motion, useAnimate } from 'motion/react'
import { useEffect, useState } from 'react'

import { useViewport } from '@/components/findex-connect/hooks/useViewport'
import { HowItWorksBlockSb } from '@/components/findex-connect/types/component-types-sb'

import { Accordion, AccordionItem } from '../ui/accordion'

type AutoAccordionProps = Pick<HowItWorksBlockSb, 'sectionHeading' | 'content'>

export const AutoAccordion: React.FC<AutoAccordionProps> = ({ content, sectionHeading }) => {
  const [activeItem, setActiveItem] = useState(0)
  const [accordionScope, animate] = useAnimate()
  const [isFocused, setIsFocused] = useState(false)
  const { isMobile } = useViewport()

  useEffect(() => {
    if (!content) return
    const controls = animate(
      '.LINE-ACTIVE',
      {
        height: ['20%', '100%'],
      },
      {
        duration: 5,
        ease: 'linear',
        onComplete() {
          setActiveItem(activeItem < content.length - 1 ? activeItem + 1 : 0)
        },
      }
    )

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // If the item has been tabbed to, pause the animation
          if (!isMobile && isFocused) {
            controls.pause()
          } else {
            controls.play()
          }
        } else {
          controls.pause()
        }
      })
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3,
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    if (accordionScope.current) {
      observer.observe(accordionScope.current)
    }

    return () => {
      controls.stop()
    }
  }, [accordionScope, activeItem, animate, content, content?.length, isFocused, isMobile])

  if (!content) return null

  return (
    <div className="AUTOACCORDION flex w-full flex-col items-center pt-0 md:pt-20">
      <div className="AUTOACCORDION-INNER max-w-container p-gutter flex w-full gap-10">
        <div className="AUTOACCORDION-TEXT-CONTAINER w-full flex-col pt-12 md:min-h-[460px] md:w-1/2 md:pt-0">
          <h2 className="mb-12 w-auto shrink self-start p-1" tabIndex={0}>
            {sectionHeading}
          </h2>

          <Accordion
            type="single"
            className="AUTOACCORDION-LIST flex flex-col gap-6"
            ref={accordionScope}
          >
            {content?.map((item, index) => {
              const isActive = index === activeItem
              const showLine = index < content.length - 1

              return (
                <AccordionItem
                  onKeyDown={(e) => e.key === 'Enter' && setActiveItem(index)}
                  is="li"
                  onFocusCapture={() => setIsFocused(true)}
                  onBlurCapture={() => setIsFocused(false)}
                  tabIndex={0}
                  value={index.toString()}
                  key={`${index}-${item.heading}`}
                  className="AUTOACCORDION-ITEM tab-outline-primary relative -mt-2 flex cursor-pointer gap-6 rounded-3xl border-none transition-all ease-in-out"
                  onClick={() => setActiveItem(index)}
                >
                  <div
                    className={
                      'LINE-TRACK absolute top-4 left-7 z-[1] h-[100%] w-[1px] bg-black/10 ' +
                      (!showLine ? ' hidden' : '')
                    }
                  />

                  {isActive && (
                    <div
                      className={
                        `${isActive && 'LINE-ACTIVE'} bg-primary-dark absolute top-6 left-7 z-[1] w-[1px]` +
                        (!showLine ? ' hidden' : '')
                      }
                    />
                  )}
                  <div
                    key={item.heading}
                    className={`ACCORDION-NUMBER text-22 z-[2] flex size-14 shrink-0 grow-0 items-center justify-center rounded-full transition-all duration-500 ease-in-out ${
                      isActive
                        ? 'bg-primary-dark border-primary-dark font-bold text-white'
                        : 'text-text border border-black/10 bg-white'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div
                    className={
                      'ACCORDION-CONTENT-INNER flex w-full flex-col overflow-hidden transition-all duration-500 ease-in-out'
                    }
                  >
                    <h3 className="!text-24 mt-4 mb-0 pb-4 lg:mb-2">{item.heading}</h3>
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          className="ACCORDION-CONTENT flex flex-col"
                          initial={{ opacity: 0, height: '0px' }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{
                            opacity: 0,
                            height: '0px',
                            transition: {
                              duration: 0.3,
                              ease: 'easeInOut',
                            },
                          }}
                        >
                          <p className="para-md">{item.text}</p>
                          {item.ctaText && (
                            <a
                              href="#"
                              onClick={(event) => {
                                event.preventDefault()
                                window.scrollTo({
                                  top: 0,
                                  behavior: 'smooth',
                                })
                              }}
                              className="para-md tab-outline-primary-sm text-primary-dark my-4 self-start rounded-lg font-bold underline underline-offset-4 focus:mx-2"
                              tabIndex={isActive ? 0 : -1}
                              onFocusCapture={() => setIsFocused(true)}
                              onBlurCapture={() => setIsFocused(false)}
                            >
                              {item.ctaText}
                            </a>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </AccordionItem>
              )
            })}
          </Accordion>
        </div>
        <div className="AUTOACCORDION-IMAGE-CONTAINER relative hidden overflow-hidden rounded-[12px] bg-black/80 md:flex md:w-1/2">
          <AnimatePresence>
            {content?.map(({ heading, image }, index) => {
              return (
                <motion.img
                  key={`${index}-image-${heading}`}
                  className="absolute top-0 left-0 h-full w-full object-cover"
                  src={image?.filename || ''}
                  alt={image?.alt || heading}
                  initial={{ opacity: 0, scale: 1.3 }}
                  animate={{
                    opacity: activeItem === index ? 1 : 0,
                    scale: 1,
                    transition: { duration: 0.3 },
                  }}
                  exit={{ opacity: 0, scale: 1.3 }}
                  transition={{
                    ease: 'easeInOut',
                    duration: 2,
                  }}
                />
              )
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
